// apps/admin/src/components/monitoring/RuntimeErrorsTab.tsx

import React, { useEffect, useState } from 'react';
import { collection, query, orderBy, limit, onSnapshot, doc, updateDoc, where, Timestamp } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { AlertCircle, CheckCircle, Clock, User, Globe, Smartphone, XCircle } from 'lucide-react';
import { Toast, ToastType } from '../common/Toast';

interface RuntimeError {
  id: string;
  appName: 'client' | 'admin';
  message: string;
  stack: string;
  componentStack: string;
  timestamp: string;
  url: string;
  userAgent: string;
  userId?: string | null;
  environment: string;
  resolved: boolean;
  occurrences: number;
  createdAt?: any;
  updatedAt?: any;
}

interface ToastState {
  message: string;
  type: ToastType;
  visible: boolean;
}

export const RuntimeErrorsTab: React.FC = () => {
  const [errors, setErrors] = useState<RuntimeError[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'unresolved'>('unresolved');
  const [appFilter, setAppFilter] = useState<'all' | 'client' | 'admin'>('all');
  const [selectedError, setSelectedError] = useState<RuntimeError | null>(null);
  const [toast, setToast] = useState<ToastState>({ message: '', type: 'info', visible: false });

  useEffect(() => {
    let q = query(
      collection(db, 'runtime_errors'),
      orderBy('timestamp', 'desc'),
      limit(100)
    );

    if (filter === 'unresolved') {
      q = query(
        collection(db, 'runtime_errors'),
        where('resolved', '==', false),
        orderBy('timestamp', 'desc'),
        limit(100)
      );
    }

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const errorData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as RuntimeError[];

        // Apply app filter client-side
        const filtered =
          appFilter === 'all'
            ? errorData
            : errorData.filter((e) => e.appName === appFilter);

        setErrors(filtered);
        setLoading(false);
      },
      (error) => {
        console.error('Error loading runtime errors:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [filter, appFilter]);

  const showToast = (message: string, type: ToastType) => {
    setToast({ message, type, visible: true });
  };

  const markAsResolved = async (errorId: string) => {
    try {
      const errorRef = doc(db, 'runtime_errors', errorId);
      await updateDoc(errorRef, {
        resolved: true,
        updatedAt: Timestamp.now(),
      });

      if (selectedError?.id === errorId) {
        setSelectedError({ ...selectedError, resolved: true });
      }
      showToast('Error marked as resolved', 'success');
    } catch (error) {
      console.error('Failed to mark error as resolved:', error);
      showToast('Failed to update error status', 'error');
    }
  };

  const getErrorStats = () => {
    const total = errors.length;
    const unresolved = errors.filter((e) => !e.resolved).length;
    const clientErrors = errors.filter((e) => e.appName === 'client').length;
    const adminErrors = errors.filter((e) => e.appName === 'admin').length;
    const affectedUsers = new Set(
      errors.filter((e) => e.userId).map((e) => e.userId)
    ).size;

    return { total, unresolved, clientErrors, adminErrors, affectedUsers };
  };

  const stats = getErrorStats();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-slate-400">Loading runtime errors...</div>
      </div>
    );
  }

  return (
    <>
      {toast.visible && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, visible: false })}
        />
      )}
      <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-400">Total Errors</p>
              <p className="text-2xl font-bold text-white">{stats.total}</p>
            </div>
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
        </div>

        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-400">Unresolved</p>
              <p className="text-2xl font-bold text-orange-400">{stats.unresolved}</p>
            </div>
            <Clock className="w-8 h-8 text-orange-500" />
          </div>
        </div>

        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-400">Client App</p>
              <p className="text-2xl font-bold text-blue-400">{stats.clientErrors}</p>
            </div>
            <Globe className="w-8 h-8 text-blue-500" />
          </div>
        </div>

        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-400">Admin App</p>
              <p className="text-2xl font-bold text-purple-400">{stats.adminErrors}</p>
            </div>
            <Smartphone className="w-8 h-8 text-purple-500" />
          </div>
        </div>

        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-400">Affected Users</p>
              <p className="text-2xl font-bold text-white">{stats.affectedUsers}</p>
            </div>
            <User className="w-8 h-8 text-slate-400" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-4">
        <div className="flex gap-4 items-center flex-wrap">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">
              Status
            </label>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as any)}
              className="bg-slate-700 text-white border border-slate-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Errors</option>
              <option value="unresolved">Unresolved Only</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">
              Application
            </label>
            <select
              value={appFilter}
              onChange={(e) => setAppFilter(e.target.value as any)}
              className="bg-slate-700 text-white border border-slate-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Apps</option>
              <option value="client">Client Only</option>
              <option value="admin">Admin Only</option>
            </select>
          </div>

          <div className="ml-auto text-sm text-slate-400">
            Showing {errors.length} error{errors.length !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* Error List & Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Error List */}
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-slate-700">
            <h2 className="text-lg font-semibold text-white">Recent Errors</h2>
          </div>

          <div className="divide-y divide-slate-700 max-h-[600px] overflow-y-auto">
            {errors.length === 0 ? (
              <div className="p-8 text-center">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <p className="text-slate-300 font-medium">No errors found</p>
                <p className="text-sm text-slate-500 mt-1">
                  {filter === 'unresolved'
                    ? 'All errors have been resolved! 🎉'
                    : 'Great job! No errors detected.'}
                </p>
              </div>
            ) : (
              errors.map((error) => (
                <div
                  key={error.id}
                  onClick={() => setSelectedError(error)}
                  className={`p-4 cursor-pointer hover:bg-slate-700/30 transition-colors ${
                    selectedError?.id === error.id
                      ? 'bg-blue-900/20 border-l-4 border-blue-500'
                      : ''
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${
                          error.appName === 'client'
                            ? 'bg-blue-500/20 text-blue-300'
                            : 'bg-purple-500/20 text-purple-300'
                        }`}
                      >
                        {error.appName}
                      </span>
                      {error.resolved && (
                        <span className="px-2 py-1 rounded text-xs font-medium bg-green-500/20 text-green-300">
                          Resolved
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-slate-500">
                      {new Date(error.timestamp).toLocaleString()}
                    </span>
                  </div>

                  <p className="font-medium text-white text-sm line-clamp-2 mb-1">
                    {error.message}
                  </p>

                  <p className="text-xs text-slate-500 truncate">{error.url}</p>

                  {error.userId && (
                    <p className="text-xs text-slate-600 mt-1">
                      User: {error.userId.substring(0, 8)}...
                    </p>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Right: Error Details */}
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-slate-700 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">Error Details</h2>
            {selectedError && !selectedError.resolved && (
              <button
                onClick={() => markAsResolved(selectedError.id)}
                className="px-3 py-1 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
              >
                Mark Resolved
              </button>
            )}
          </div>

          {selectedError ? (
            <div className="p-4 space-y-4 max-h-[600px] overflow-y-auto">
              {/* Status Badge */}
              {selectedError.resolved && (
                <div className="bg-green-500/20 border border-green-500/50 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    <span className="text-sm font-medium text-green-300">
                      This error has been marked as resolved
                    </span>
                  </div>
                </div>
              )}

              {/* Error Message */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">
                  Error Message
                </label>
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                  <p className="text-red-300 font-mono text-sm break-words">
                    {selectedError.message}
                  </p>
                </div>
              </div>

              {/* Metadata Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">
                    Application
                  </label>
                  <p className="text-sm text-white capitalize">{selectedError.appName}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">
                    Environment
                  </label>
                  <p className="text-sm text-white capitalize">{selectedError.environment}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">
                    Occurrences
                  </label>
                  <p className="text-sm text-white">{selectedError.occurrences}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">
                    Timestamp
                  </label>
                  <p className="text-sm text-white">
                    {new Date(selectedError.timestamp).toLocaleString()}
                  </p>
                </div>
              </div>

              {/* URL */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">
                  Page URL
                </label>
                <p className="text-sm break-all bg-slate-900/50 p-2 rounded text-slate-300">
                  {selectedError.url}
                </p>
              </div>

              {/* User ID */}
              {selectedError.userId && (
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">
                    User ID
                  </label>
                  <p className="text-sm font-mono bg-slate-900/50 p-2 rounded text-slate-300">
                    {selectedError.userId}
                  </p>
                </div>
              )}

              {/* Stack Trace */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">
                  Stack Trace
                </label>
                <pre className="bg-slate-950 text-slate-300 rounded-lg p-3 text-xs overflow-x-auto border border-slate-800">
                  {selectedError.stack}
                </pre>
              </div>

              {/* Component Stack */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">
                  React Component Stack
                </label>
                <pre className="bg-slate-900/50 text-slate-400 rounded-lg p-3 text-xs overflow-x-auto border border-slate-800">
                  {selectedError.componentStack}
                </pre>
              </div>

              {/* User Agent */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">
                  User Agent
                </label>
                <p className="text-xs text-slate-400 break-all bg-slate-900/50 p-2 rounded">
                  {selectedError.userAgent}
                </p>
              </div>
            </div>
          ) : (
            <div className="p-8 text-center text-slate-500">
              <XCircle className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <p>Select an error from the list to view details</p>
            </div>
          )}
        </div>
      </div>

      {/* Info Note */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
        <p className="text-sm text-blue-300">
          <strong>Real-time Monitoring:</strong> This dashboard shows runtime errors caught by
          error boundaries in both client and admin applications. Errors are logged automatically
          when users encounter issues. All errors respect AU privacy law (no PII logged).
        </p>
      </div>
    </div>
    </>
  );
};